import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import toast from "react-hot-toast";
import { useCreateService } from "../../hooks/services/useCreateService";
import { useUpdateService } from "../../hooks/services/useUpdateService";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";

const FormAddEditService = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Form States
    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [slug, setSlug] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [image, setImage] = useState(null);
    const [showInHome, setShowInHome] = useState(true);

    // Hooks
    const { createServiceMutate, isPending: isCreating } = useCreateService();
    const { updateServiceMutate, isPending: isUpdating } = useUpdateService();

    const loadingSubmit = isCreating || isUpdating;

    // Auto-slug generation from English title (only in English mode and slug is empty)
    useEffect(() => {
        if (activeLang === "en" && titleEn && (!isEdit || !slug)) {
            setSlug(slugify(titleEn));
        }
    }, [titleEn, activeLang, isEdit, slug]);

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData?.title?.ar || currentData?.title || "");
            setTitleEn(currentData?.title?.en || currentData?.title || "");
            setSlug(currentData?.slug || "");
            setDescriptionAr(currentData?.description?.ar || currentData?.description || "");
            setDescriptionEn(currentData?.description?.en || currentData?.description || "");
            setImage(currentData?.image || null);
            setShowInHome(currentData?.show_in_home ?? true);
        }
    }, [isEdit, currentData]);

    const handleSlugChange = (e) => {
        const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
        setSlug(val);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!slug) {
            toast.error(isArabic ? "يرجى إدخال الرابط (Slug)" : "Please enter a slug");
            return;
        }

        const payload = {
            title: { ar: titleAr || titleEn, en: titleEn || titleAr },
            slug,
            description: { ar: descriptionAr || descriptionEn, en: descriptionEn || descriptionAr },
            show_in_home: showInHome,
            sort_order: 0,
        };

        if (image) {
            payload.image = image;
        }

        if (isEdit) {
            updateServiceMutate({ id: currentData.id, data: payload });
        } else {
            createServiceMutate(payload);
        }

        if (onSubmitSuccess) onSubmitSuccess();
    };

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل الخدمة" : "Edit Service") : (isArabic ? "إضافة خدمة جديدة" : "Add New Service")}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(l) => setActiveLang(l)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Image at the top center */}
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <FormInput
                            label={isArabic ? "الصورة" : "Image"}
                            type="file"
                            name="image"
                            value={image}
                            onChange={(e) => setImage(e.target.value)}
                            width={120}
                            height={120}
                        />
                    </Grid>

                    {/* Multilingual Titles */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="عنوان الخدمة (عربي)"
                                placeholder="أدخل عنوان الخدمة بالعربي"
                                type="text"
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Service Title (English)"
                                placeholder="Enter service title in English"
                                type="text"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                required
                            />
                        </Grid>
                    )}

                    {/* Slug */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug - إنجليزي فقط)" : "Slug (English only)"}
                            placeholder="service-name"
                            type="text"
                            value={slug}
                            onChange={handleSlugChange}
                            disabled={activeLang === "en"}
                            required
                            helperText={activeLang === "ar" ? (isArabic ? "يجب إدخال الرابط باللغة الإنجليزية" : "Slug must be entered in English") : ""}
                        />
                    </Grid>

                    {/* Multilingual Descriptions */}
                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <FormInput
                                label="وصف الخدمة (عربي)"
                                placeholder="أدخل وصف الخدمة بالعربي"
                                type="textarea"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                minRows={3}
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <FormInput
                                label="Service Description (English)"
                                placeholder="Enter service description in English"
                                type="textarea"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                minRows={3}
                            />
                        </Grid>
                    )}

                    {/* Show In Home Switch */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "عرض في الرئيسية" : "Show in Home"}
                            type="switch"
                            name="show_in_home"
                            checked={showInHome}
                            onChange={(e) => setShowInHome(e.target.checked)}
                        />
                    </Grid>
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "حفظ" : "Save"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditService;